// EVA NEXIO.HUB bottle error 검출 웹앱 App.js

import React, { useState, useEffect, useRef } from 'react';
import { Camera, AlertCircle, Users, FileText, Settings, Play, Upload } from 'lucide-react';
import logoTitle from './assets/logo-title.png';

// ============ 이미지/영상 import 추가 ============
import defaultVideo from './assets/default-video.gif';
import defect1 from './assets/defect1.jpg';
import defect2 from './assets/defect2.jpg';
import defect3 from './assets/defect3.jpg';
import defect4 from './assets/defect4.jpg';

// ============ 기본 파일 경로 설정 ============
const DEFAULT_VIDEO = defaultVideo;
const DEFAULT_IMAGES = [
  defect1,
  defect2,
  defect3,
  defect4
];

export default function App() {
  const [videoFile, setVideoFile] = useState(null);
  const [defectImages, setDefectImages] = useState([]);
  const [currentDefect, setCurrentDefect] = useState(null);
  const [stats, setStats] = useState({
    defectRate: 2.4,
    production: 1247,
    detections: 0
  });
  const [alerts, setAlerts] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const videoRef = useRef(null);
  const defectIndexRef = useRef(0);

  // ============ 자동 로드 추가 ============
  useEffect(() => {
    // 기본 영상 로드
    setVideoFile(DEFAULT_VIDEO);
    
    // 기본 불량 이미지 로드
    setDefectImages(DEFAULT_IMAGES);
  }, []);

  // 파일을 Base64로 변환
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // 동영상 업로드
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        alert('파일이 너무 큽니다. 50MB 이하로 선택해주세요.');
        return;
      }
      
      if (file.type.startsWith('video/') || file.type === 'image/gif') {
        try {
          const base64 = await fileToBase64(file);
          setVideoFile(base64);
        } catch (error) {
          alert('파일 로드 실패');
        }
      }
    }
  };

  // 이미지 업로드
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    try {
      const imagePromises = files
        .filter(f => f.type.startsWith('image/'))
        .map(f => fileToBase64(f));
      const base64Images = await Promise.all(imagePromises);
      setDefectImages(base64Images);
    } catch (error) {
      alert('이미지 로드 실패');
    }
  };

  // 시뮬레이션 시작
  const startSimulation = () => {
    if (defectImages.length === 0) {
      alert('불량 이미지를 먼저 업로드해주세요.');
      return;
    }
    if (!videoFile) {
      alert('동영상을 먼저 업로드해주세요.');
      return;
    }
    setIsRunning(true);
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.play().catch(e => console.log('재생 시작'));
      }
    }, 100);
  };

  // 자동 불량 감지
  useEffect(() => {
    if (!isRunning || defectImages.length === 0) return;

    const interval = setInterval(() => {
      const defectTypes = ['병 변형', '병목 변형', '병 바닥 불량', '병 두께 불균일', '병 찌그러짐'];
      const randomType = defectTypes[Math.floor(Math.random() * defectTypes.length)];
      const confidence = (Math.random() * 0.15 + 0.85).toFixed(2);
      
      const newDefect = {
        id: `DEF-${Date.now()}`,
        type: randomType,
        confidence: confidence,
        image: defectImages[defectIndexRef.current % defectImages.length],
        time: new Date().toLocaleTimeString('ko-KR')
      };

      defectIndexRef.current += 1;
      setCurrentDefect(newDefect);
      
      setStats(prev => ({
        defectRate: (Math.random() * 2 + 2).toFixed(1),
        production: prev.production + Math.floor(Math.random() * 5 + 3),
        detections: prev.detections + 1
      }));

      setAlerts(prev => [{
        message: `${randomType} 감지 (신뢰도: ${(confidence * 100).toFixed(0)}%)`,
        time: new Date().toLocaleTimeString('ko-KR')
      }, ...prev.slice(0, 4)]);

    }, 5000);

    return () => clearInterval(interval);
  }, [isRunning, defectImages]);

  return (
    <div className="min-h-screen max-h-screen bg-gray-900 text-white flex flex-col overflow-hidden">
      {/* 헤더 */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src={logoTitle}
              alt="EVA NEXIO HUB" 
              className="h-14 sm:h-16 md:h-20 w-auto max-w-none transition-all duration-300"
            />
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm">시스템 정상</span>
            </div>
            <span className="text-sm text-gray-400">관리자: 홍길동</span>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* 좌측 메뉴 */}
        <aside className="w-64 bg-gray-800 border-r border-gray-700 p-4 flex flex-col overflow-hidden">
          <nav className="space-y-2">
            <button className="w-full flex items-center space-x-3 px-4 py-3 bg-blue-600 rounded-lg">
              <Camera className="w-5 h-5" />
              <span>실시간 모니터링</span>
            </button>
            <button className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-700 rounded-lg">
              <AlertCircle className="w-5 h-5" />
              <span>불량 분석</span>
            </button>
            <button className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-700 rounded-lg">
              <Users className="w-5 h-5" />
              <span>협업 자동화</span>
            </button>
            <button className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-700 rounded-lg">
              <FileText className="w-5 h-5" />
              <span>리포트 보기</span>
            </button>
            <button className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-700 rounded-lg">
              <Settings className="w-5 h-5" />
              <span>설정</span>
            </button>
          </nav>

          <div className="mt-8 p-4 bg-gray-700 rounded-lg">
            <h3 className="text-sm font-semibold mb-3">통계</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">불량률</span>
                <span className="text-red-400 font-bold">{stats.defectRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">생산량</span>
                <span className="text-green-400 font-bold">{stats.production}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">감지 횟수</span>
                <span className="text-blue-400 font-bold">{stats.detections}</span>
              </div>
            </div>
          </div>
        </aside>

        {/* 메인 콘텐츠 */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* 상단: 모니터링 + 분석 */}
          <div className="flex flex-1 border-b border-gray-700 overflow-hidden">
            {/* 실시간 모니터링 */}
            <section className="flex-1 p-4 border-r border-gray-700 flex flex-col overflow-hidden">
              <div className="h-full flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-bold flex items-center">
                    <Camera className="w-5 h-5 mr-2 text-blue-400" />
                    실시간 공정 모니터링
                  </h2>
                  <div className="flex items-center space-x-2">
                    {!isRunning && (
                      <button
                        onClick={startSimulation}
                        className="flex items-center space-x-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded text-sm"
                      >
                        <Play className="w-4 h-4" />
                        <span>시작</span>
                      </button>
                    )}
                    <label className="cursor-pointer px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-sm">
                      변경
                      <input
                        type="file"
                        accept="video/mp4,video/webm,image/gif"
                        onChange={handleVideoUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                <div className="flex-1 bg-black rounded-lg overflow-hidden relative">
                  {videoFile && (videoFile.startsWith('data:image/gif') || videoFile.endsWith('.gif')) ? (
                    <img src={videoFile} alt="공정" className="w-full h-full object-cover" />
                  ) : videoFile && (
                    <video ref={videoRef} autoPlay loop muted playsInline className="w-full h-full object-cover">
                      <source src={videoFile} type="video/mp4" />
                    </video>
                  )}
                  {isRunning && (
                    <div className="absolute top-3 left-3 bg-red-600 px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
                      <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                      <span>YOLO 분석 중</span>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* 불량 분석 */}
            <section className="flex-1 p-4 flex flex-col overflow-hidden">
              <div className="h-full flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-bold flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2 text-red-400" />
                    불량 제품 분석
                  </h2>
                  <label className="cursor-pointer px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-sm">
                    변경
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                </div>

                {!currentDefect ? (
                  <div className="flex-1 bg-gray-800 rounded-lg flex items-center justify-center">
                    <p className="text-sm text-gray-400">시작 버튼을 눌러주세요</p>
                  </div>
                ) : (
                  <div className="flex-1 bg-gray-800 rounded-lg p-3 flex flex-col overflow-hidden">
                    <div className="mb-3 flex-shrink-0">
                      <img src={currentDefect.image} alt="불량" className="w-full h-40 object-contain bg-black rounded" />
                    </div>
                    <div className="space-y-2 text-sm overflow-auto flex-1">
                      <div className="bg-gray-700 p-2 rounded">
                        <div className="text-xs text-gray-400">제품 ID</div>
                        <div className="font-bold text-sm">{currentDefect.id}</div>
                      </div>
                      <div className="bg-gray-700 p-2 rounded">
                        <div className="text-xs text-gray-400">불량 유형</div>
                        <div className="font-bold text-red-400">{currentDefect.type}</div>
                      </div>
                      <div className="bg-gray-700 p-2 rounded">
                        <div className="text-xs text-gray-400">신뢰도</div>
                        <div className="font-bold text-green-400">{(currentDefect.confidence * 100).toFixed(0)}%</div>
                      </div>
                      <div className="bg-gray-700 p-2 rounded">
                        <div className="text-xs text-gray-400">감지 시각</div>
                        <div className="font-bold text-sm">{currentDefect.time}</div>
                      </div>
                      <div className="bg-blue-900 p-2 rounded border border-blue-700">
                        <div className="text-xs font-bold mb-1">원인 분석 (AI)</div>
                        <div className="text-xs">생수병 몸체 변형 - 사출 온도/압력 이상</div>
                      </div>
                      <div className="bg-orange-900 p-2 rounded border border-orange-700">
                        <div className="text-xs font-bold mb-1">조치 방법</div>
                        <div className="text-xs">사출기 온도/압력 점검 필요</div>
                      </div>
                      <button className="w-full py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-xs">
                        📄 보고서 생성
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* 하단: 협업 자동화 */}
          <section className="p-4 h-64 flex flex-col overflow-hidden">
            <h2 className="text-lg font-bold mb-3 flex items-center">
              <Users className="w-5 h-5 mr-2 text-green-400" />
              협업 자동화
            </h2>

            <div className="h-[calc(100%-2rem)] flex space-x-3 overflow-hidden">
              {/* 알림 */}
              <div className="flex-1 bg-gray-800 rounded-lg p-3 overflow-auto">
                <div className="mb-3 p-2 bg-blue-900 rounded border border-blue-700">
                  <div className="text-xs font-bold mb-1">자동 태그된 부서</div>
                  <div className="flex flex-wrap gap-1">
                    <span className="px-2 py-0.5 bg-blue-700 rounded-full text-xs">품질팀 (QC)</span>
                    <span className="px-2 py-0.5 bg-blue-700 rounded-full text-xs">설비정비팀</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-xs font-bold text-gray-400">실시간 알림</h3>
                  {alerts.slice(0, 4).map((alert, idx) => (
                    <div key={idx} className="bg-gray-700 p-2 rounded border-l-4 border-yellow-500">
                      <div className="text-xs">{alert.message}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{alert.time}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* LLM 협업 */}
              <div className="flex-1 bg-gray-800 rounded-lg p-3 overflow-auto">
                {currentDefect ? (
                  <div>
                    <div className="text-xs font-bold mb-2">💬 LLM 협업창</div>
                    <div className="space-y-1.5 text-xs mb-3">
                      <div className="bg-gray-600 p-1.5 rounded">
                        <span className="font-bold">AI:</span> 생수병 형태 불량 감지됨
                      </div>
                      <div className="bg-blue-600 p-1.5 rounded text-right">
                        <span className="font-bold">사용자:</span> 정비팀 호출
                      </div>
                      <div className="bg-gray-600 p-1.5 rounded">
                        <span className="font-bold">AI:</span> 정비팀 알림 전송 완료
                      </div>
                    </div>
                    <button className="w-full py-1.5 bg-green-600 hover:bg-green-700 rounded text-xs">
                      ✓ 승인 요청 전송
                    </button>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-xs text-gray-400">
                    불량 감지 대기 중...
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
